import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { FeatureFlagsService, PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-publish-policy-dialog',
  templateUrl: './publish-policy.dialog.html',
  standalone: false,
})
export class PublishPolicyDialog {
  constructor(
    private policySvc: PolicyService,
    private toastSvc: ToastService,
    private featureFlagsSvc: FeatureFlagsService
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy?: Policy.Model | null;

  saving = false;

  private dynamicRatingEnabled$ = this.featureFlagsSvc.isEnabled('DYNAMIC_RATING');

  planConfigurationStatus$ = combineLatest([this.policy$, this.dynamicRatingEnabled$]).pipe(
    map(([policy, dynamicRatingEnabled]) => {
      if (!policy || !policy.plans?.length) {
        return { allConfigured: false, issues: ['No plans have been added.'] };
      }

      const issues: string[] = [];

      for (const plan of policy.plans) {
        const planName = plan.title || 'Unnamed plan';
        if (!plan.title) issues.push(`Plan "${planName}" is missing a title.`);
        if (!plan.coverageConfigs) {
          issues.push(`Plan "${planName}" has no coverage configurations.`);
          continue;
        }

        const termConfigs = Object.entries(plan.coverageConfigs);
        if (termConfigs.length === 0) {
          issues.push(`Plan "${planName}" has no coverage configurations for any term.`);
          continue;
        }

        let configuredTermFound = false;

        termConfigs.forEach(([termId, configs]) => {
          const termName = policy.terms?.find(term => term.id === termId)?.title || `Term ${termId}`;
          if (!configs?.length) return;

          configuredTermFound = true;

          configs.forEach(config => {
            if (!config) {
              issues.push(`Plan "${planName}" has an empty coverage configuration for ${termName}.`);
              return;
            }

            const missingFields: string[] = [];
            const coverageTitle =
              config.coverage?.title || policy.coverages?.find(coverage => coverage.id === config.coverageId)?.title;
            const coverageLabel = coverageTitle ? ` / ${coverageTitle}` : '';

            if (
              !config.requirement ||
              config.requirement.defaultValue === '' ||
              config.requirement.defaultValue === null
            )
              missingFields.push('Included In');
            if (!config.deductible || (config.deductible.defaultValue === null && !config.deductible.valuePropertyPath))
              missingFields.push('Deductible');
            // Only validate price if payment schedules exist for this term and dynamic rating is disabled
            const hasPaymentScheduleForTerm = policy.terms.length
              ? policy.terms.find(term => term.id === termId)?.paymentSchedules?.length
              : false;
            if (
              !dynamicRatingEnabled &&
              hasPaymentScheduleForTerm &&
              (!config.price || (config.price.defaultValue === null && !config.price.valuePropertyPath))
            )
              missingFields.push('Price');
            if (
              !config.liabilityLimit ||
              (config.liabilityLimit.defaultValue === null && !config.liabilityLimit.valuePropertyPath)
            )
              missingFields.push('Liability Limit');
            if (!config.liabilityGroups || config.liabilityGroups.defaultValue === null)
              missingFields.push('Liability Groups');

            if (missingFields.length) {
              issues.push(`Plan "${planName}" (${termName}${coverageLabel}) is missing: ${missingFields.join(', ')}.`);
            }
          });
        });

        if (!configuredTermFound) {
          issues.push(`Plan "${planName}" has no coverages configured for any term.`);
        }
      }

      return { allConfigured: issues.length === 0, issues };
    })
  );

  emailConfigurationStatus$ = this.policy$.pipe(
    map(policy => {
      if (!policy || !policy.emails?.length) {
        return { allConfigured: false, issues: ['No email templates have been added.'] };
      }

      const eventDrivenEmails = policy.emails.filter(
        email => !email.deliveryMode || email.deliveryMode === Policy.DeliveryMode.EVENT_DRIVEN
      );

      if (!eventDrivenEmails.length) {
        return { allConfigured: false, issues: ['No event-driven email templates have been added.'] };
      }

      const issues: string[] = [];
      for (const email of eventDrivenEmails) {
        const missingFields: string[] = [];
        if (!email.subject) missingFields.push('Subject');
        if (!email.content) missingFields.push('Content');

        if (missingFields.length) {
          const disabled = email.enabled === false ? ' (disabled)' : '';
          issues.push(
            `Email "${email.templateLabel || email.type}"${disabled} is missing: ${missingFields.join(', ')}.`
          );
        }
      }

      return { allConfigured: issues.length === 0, issues };
    })
  );

  open() {
    this.dialog.open();
  }

  close() {
    this.dialog.close();
  }

  cancel() {
    this.close();
  }

  async publish() {
    this.saving = true;

    if (!this.policy) throw new Error('Unable to publish policy, policy not found');

    if (this.policy.status !== Policy.Status.DRAFT) {
      throw new Error(`Unable to publish policy with status: ${this.policy.status}`);
    }

    const publishedPolicy = await this.policySvc.publish(this.policy.id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });

      this.close();
      this.saving = false;
    });

    const publishedPolicyId =
      publishedPolicy && typeof publishedPolicy === 'object' && 'id' in publishedPolicy
        ? publishedPolicy.id
        : undefined;
    if (publishedPolicyId) {
      this.toastSvc.show({
        type: 'success',
        title: 'Published policy',
        text: '<strong>' + this.policy?.title + '</strong> has been successfully published.',
      });

      this.close();
      this.saving = false;
      this.policySvc.refreshPolicy();
    }
  }
}
